import { useEffect, useRef, useState, useCallback } from 'react';

export const useWebRTC = (socket, partnerId) => {
  const [localStream, setLocalStream] = useState(null);
  const [remoteStream, setRemoteStream] = useState(null);
  const [isCallActive, setIsCallActive] = useState(false);
  const [isConnecting, setIsConnecting] = useState(false);
  const [error, setError] = useState(null);
  const [isAudioEnabled, setIsAudioEnabled] = useState(true);
  const [isVideoEnabled, setIsVideoEnabled] = useState(true);

  const localVideoRef = useRef(null);
  const remoteVideoRef = useRef(null);
  const peerConnectionRef = useRef(null);
  const localStreamRef = useRef(null);

  // ICE servers configuration
  const iceServers = {
    iceServers: [
      { urls: 'stun:stun.l.google.com:19302' },
      { urls: 'stun:stun1.l.google.com:19302' },
    ],
  };

  // Initialize peer connection
  const initializePeerConnection = useCallback(() => {
    if (!socket) return null;

    const peerConnection = new RTCPeerConnection(iceServers);

    peerConnection.onicecandidate = (event) => {
      if (event.candidate) {
        console.log('Sending ICE candidate:', event.candidate);
        socket.emit('webrtc_ice_candidate', {
          candidate: event.candidate,
        });
      }
    };

    peerConnection.ontrack = (event) => {
      console.log('Remote track received:', event.streams[0]);
      setRemoteStream(event.streams[0]);
      if (remoteVideoRef.current) {
        remoteVideoRef.current.srcObject = event.streams[0];
      }
    };

    peerConnection.onconnectionstatechange = () => {
      console.log('Connection state:', peerConnection.connectionState);
      if (peerConnection.connectionState === 'connected') {
        setIsCallActive(true);
        setIsConnecting(false);
      } else if (peerConnection.connectionState === 'disconnected') {
        setIsCallActive(false);
        setIsConnecting(false);
      }
    };

    return peerConnection;
  }, [socket]);

  // Get user media
  const getUserMedia = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: true,
        audio: true,
      });
      
      setLocalStream(stream);
      localStreamRef.current = stream;
      
      if (localVideoRef.current) {
        localVideoRef.current.srcObject = stream;
      }
      
      return stream;
    } catch (err) {
      console.error('Error accessing media devices:', err);
      
      // Provide specific error messages
      if (err.name === 'NotAllowedError') {
        setError('Camera/microphone access denied. Please allow permissions and try again.');
      } else if (err.name === 'NotFoundError') {
        setError('No camera/microphone found. Please check your device.');
      } else if (err.name === 'NotSupportedError') {
        setError('Camera/microphone access not supported on this device.');
      } else {
        setError('Failed to access camera/microphone. Please check permissions and try again.');
      }
      
      throw err;
    }
  };

  // Start call
  const startCall = async () => {
    if (!socket || !partnerId) return;

    try {
      setIsConnecting(true);
      setError(null);

      const stream = await getUserMedia();
      const peerConnection = initializePeerConnection();
      
      if (!peerConnection) return;

      peerConnectionRef.current = peerConnection;

      // Add local stream to peer connection
      stream.getTracks().forEach((track) => {
        peerConnection.addTrack(track, stream);
      });

      // Create and send offer
      const offer = await peerConnection.createOffer();
      await peerConnection.setLocalDescription(offer);

      console.log('Sending offer:', offer);
      socket.emit('webrtc_offer', { offer });
    } catch (err) {
      console.error('Error starting call:', err);
      setError('Failed to start call');
      setIsConnecting(false);
    }
  };

  // Handle incoming offer
  const handleOffer = async (offer) => {
    if (!socket) return;

    try {
      setIsConnecting(true);
      setError(null);

      const stream = await getUserMedia();
      const peerConnection = initializePeerConnection();
      
      if (!peerConnection) return;

      peerConnectionRef.current = peerConnection;

      // Add local stream to peer connection
      stream.getTracks().forEach((track) => {
        peerConnection.addTrack(track, stream);
      });

      // Set remote description and create answer
      await peerConnection.setRemoteDescription(offer);
      const answer = await peerConnection.createAnswer();
      await peerConnection.setLocalDescription(answer);

      console.log('Sending answer:', answer);
      socket.emit('webrtc_answer', { answer });
    } catch (err) {
      console.error('Error handling offer:', err);
      setError('Failed to join call');
      setIsConnecting(false);
    }
  };

  // Handle incoming answer
  const handleAnswer = async (answer) => {
    if (!peerConnectionRef.current) return;

    try {
      await peerConnectionRef.current.setRemoteDescription(answer);
      console.log('Answer set successfully');
    } catch (err) {
      console.error('Error handling answer:', err);
      setError('Failed to connect call');
    }
  };

  // Handle ICE candidate
  const handleIceCandidate = async (candidate) => {
    if (!peerConnectionRef.current) return;

    try {
      await peerConnectionRef.current.addIceCandidate(candidate);
      console.log('ICE candidate added successfully');
    } catch (err) {
      console.error('Error adding ICE candidate:', err);
    }
  };

  // Toggle audio
  const toggleAudio = () => {
    if (localStreamRef.current) {
      const audioTrack = localStreamRef.current.getAudioTracks()[0];
      if (audioTrack) {
        audioTrack.enabled = !audioTrack.enabled;
        setIsAudioEnabled(audioTrack.enabled);
      }
    }
  };

  // Toggle video
  const toggleVideo = () => {
    if (localStreamRef.current) {
      const videoTrack = localStreamRef.current.getVideoTracks()[0];
      if (videoTrack) {
        videoTrack.enabled = !videoTrack.enabled;
        setIsVideoEnabled(videoTrack.enabled);
      }
    }
  };

  // End call
  const endCall = () => {
    if (peerConnectionRef.current) {
      peerConnectionRef.current.close();
      peerConnectionRef.current = null;
    }

    if (localStreamRef.current) {
      localStreamRef.current.getTracks().forEach((track) => {
        track.stop();
      });
      localStreamRef.current = null;
    }

    setLocalStream(null);
    setRemoteStream(null);
    setIsCallActive(false);
    setIsConnecting(false);
    setError(null);
  };

  // Socket event listeners
  useEffect(() => {
    if (!socket) return;

    socket.on('webrtc_offer', ({ offer }) => {
      console.log('Received offer:', offer);
      handleOffer(offer);
    });

    socket.on('webrtc_answer', ({ answer }) => {
      console.log('Received answer:', answer);
      handleAnswer(answer);
    });

    socket.on('webrtc_ice_candidate', ({ candidate }) => {
      console.log('Received ICE candidate:', candidate);
      handleIceCandidate(candidate);
    });

    socket.on('partner_disconnected', () => {
      endCall();
    });

    return () => {
      socket.off('webrtc_offer');
      socket.off('webrtc_answer');
      socket.off('webrtc_ice_candidate');
      socket.off('partner_disconnected');
    };
  }, [socket]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      endCall();
    };
  }, []);

  return {
    localStream,
    remoteStream,
    isCallActive,
    isConnecting,
    error,
    isAudioEnabled,
    isVideoEnabled,
    localVideoRef,
    remoteVideoRef,
    startCall,
    endCall,
    toggleAudio,
    toggleVideo,
  };
}; 